// packages/web/src/components/teach/StudentProgressTable.tsx
import React, { useState } from 'react';
import { useHomeworkStore } from '../../stores/homeworkStore';
import { ArrowUpDown, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';

type SortField = 'student_name' | 'completion_percentage' | 'last_activity';
type SortOrder = 'asc' | 'desc';

export const StudentProgressTable: React.FC = () => {
  const { homeworkProgress } = useHomeworkStore();
  const [sortField, setSortField] = useState<SortField>('student_name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  if (!homeworkProgress || !homeworkProgress.student_progress) {
    return <p className="text-sm text-gray-500">暂无数据</p>;
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedProgress = [...homeworkProgress.student_progress].sort((a, b) => {
    let comparison = 0;
    
    switch (sortField) {
      case 'student_name':
        comparison = a.student_name.localeCompare(b.student_name, 'zh-CN');
        break;
      case 'completion_percentage':
        comparison = a.completion_percentage - b.completion_percentage;
        break;
      case 'last_activity':
        const dateA = a.last_activity ? new Date(a.last_activity).getTime() : 0;
        const dateB = b.last_activity ? new Date(b.last_activity).getTime() : 0;
        comparison = dateA - dateB;
        break;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-600" />;
      case 'overdue':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'not_started':
        return <XCircle className="w-5 h-5 text-gray-400" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      completed: '已完成',
      in_progress: '进行中',
      overdue: '已逾期',
      not_started: '未开始',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      completed: 'bg-green-100 text-green-800',
      in_progress: 'bg-blue-100 text-blue-800',
      overdue: 'bg-red-100 text-red-800',
      not_started: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-3 text-left">
              <button
                onClick={() => handleSort('student_name')}
                className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
              >
                学生姓名
                <ArrowUpDown className="w-4 h-4" />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              班级
            </th>
            <th className="px-4 py-3 text-left">
              <button
                onClick={() => handleSort('completion_percentage')}
                className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
              >
                完成进度
                <ArrowUpDown className="w-4 h-4" />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              状态
            </th>
            <th className="px-4 py-3 text-left">
              <button
                onClick={() => handleSort('last_activity')}
                className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
              >
                最后活动
                <ArrowUpDown className="w-4 h-4" />
              </button>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedProgress.map((progress) => (
            <tr key={progress.student_id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <div className="text-sm font-medium text-gray-900">{progress.student_name}</div>
              </td>
              
              <td className="px-4 py-3">
                <div className="text-sm text-gray-600">{progress.class_name || '-'}</div>
              </td>
              
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-600">
                        {progress.completed_exercises}/{progress.total_exercises}
                      </span>
                      <span className="font-medium text-gray-900">
                        {progress.completion_percentage}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          progress.completion_percentage === 100
                            ? 'bg-green-500'
                            : progress.status === 'overdue'
                            ? 'bg-red-500'
                            : 'bg-blue-500'
                        }`}
                        style={{ width: `${progress.completion_percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              </td>
              
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  {getStatusIcon(progress.status)}
                  <span className={`
                    inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                    ${getStatusColor(progress.status)}
                  `}>
                    {getStatusLabel(progress.status)}
                  </span>
                </div>
              </td>
              
              <td className="px-4 py-3">
                <div className="text-sm text-gray-600">
                  {progress.last_activity
                    ? new Date(progress.last_activity).toLocaleString('zh-CN', {
                        month: 'numeric',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '-'}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {sortedProgress.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">暂无学生数据</p>
        </div>
      )}
    </div>
  );
};
